/**
 * Enhanced MutationObserver compatibility fix for Vue 3 migration
 * This file provides a robust polyfill and safety wrapper for MutationObserver
 * to prevent null node errors during the Vue 3 migration phase.
 *
 * Addresses specific issues with:
 * - @vuepic/vue-datepicker
 * - @vueup/vue-quill (QuillEditor)
 * - bootstrap-vue-next
 * - vue-final-modal
 * - Other third-party libraries during Vue 3 migration
 */

// IMMEDIATE EXECUTION: Apply fix as soon as this module loads
(function () {
  "use strict";

  // Store the original MutationObserver immediately
  if (!window.MutationObserver) {
    console.warn("MutationObserver not available in this environment");
    return;
  }

  const OriginalMutationObserver = window.MutationObserver;

  // Debug flag - set to false in production
  const DEBUG_MUTATION_OBSERVER = process.env.NODE_ENV === "development";

  // Track problematic libraries for debugging
  const libraryCallTracker = new Map();

  // Page reload detection
  let isPageReloading = false;
  let pageLoadTime = Date.now();

  // Detect page reload vs fresh load
  window.addEventListener("beforeunload", () => {
    isPageReloading = true;
  });

  // Reset after page is stable
  window.addEventListener("load", () => {
    setTimeout(() => {
      isPageReloading = false;
      pageLoadTime = Date.now();
    }, 500); // Give extra time for Vue components to initialize
  });

  // Additional stability check for recent page loads
  function isRecentPageLoad() {
    return Date.now() - pageLoadTime < 2000; // 2 seconds after page load
  }

  // Helper function to get stack trace info
  function getCallerInfo() {
    if (!DEBUG_MUTATION_OBSERVER) return null;

    try {
      const stack = new Error().stack;
      const lines = stack.split("\n");

      // Look for known problematic libraries in the stack
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        if (
          line.includes("vue-datepicker") ||
          line.includes("vue-quill") ||
          line.includes("bootstrap-vue") ||
          line.includes("vue-final-modal") ||
          line.includes("floating-vue")
        ) {
          return line.trim();
        }
      }

      // Return the first external library call if found
      for (let i = 2; i < Math.min(lines.length, 10); i++) {
        const line = lines[i];
        if (line.includes("node_modules") || line.includes(".js:")) {
          return line.trim();
        }
      }
    } catch (e) {
      // Ignore stack trace errors
    }

    return null;
  }

  // Enhanced validation function
  function isValidObserverTarget(target) {
    // Null or undefined check - including empty string which causes the main error
    if (!target || target === "" || target === null || target === undefined) {
      return {
        valid: false,
        reason: "Target is null, undefined, or empty string",
      };
    }

    // Check if it's a Node-like object with additional safety
    if (typeof target !== "object") {
      return { valid: false, reason: "Target is not an object" };
    }

    // Additional safety check for page reload scenarios where target might be in invalid state
    try {
      // Test if target has basic Node properties - this can throw during page reload
      if (!target.nodeType && target.nodeType !== 0) {
        return {
          valid: false,
          reason: "Target does not have a nodeType property",
        };
      }
    } catch (error) {
      return {
        valid: false,
        reason: `Target access error during page reload: ${error.message}`,
      };
    }

    // Check for nodeType property
    if (typeof target.nodeType !== "number") {
      return { valid: false, reason: "Target does not have a valid nodeType" };
    }

    // Validate nodeType range (1-12 are valid Node types)
    if (target.nodeType < 1 || target.nodeType > 12) {
      return { valid: false, reason: `Invalid nodeType: ${target.nodeType}` };
    }

    // Check if it's a Document or Element node that should be connected
    if (
      target.nodeType === Node.ELEMENT_NODE ||
      target.nodeType === Node.TEXT_NODE
    ) {
      try {
        if (typeof target.isConnected === "boolean" && !target.isConnected) {
          return {
            valid: false,
            reason: "Element/Text node is not connected to DOM",
          };
        }
      } catch (error) {
        return {
          valid: false,
          reason: `isConnected check failed: ${error.message}`,
        };
      }
    }

    // Additional checks for common problematic cases
    if (target.nodeType === Node.ELEMENT_NODE) {
      try {
        // Check if element has been destroyed/removed
        if (target.parentNode === null && target !== document.documentElement) {
          return {
            valid: false,
            reason: "Element has no parent and is not document root",
          };
        }

        // Check for Vue.js specific markers that indicate destroyed components
        if (target.__vue__ && target.__vue__._isDestroyed) {
          return { valid: false, reason: "Vue component has been destroyed" };
        }
      } catch (error) {
        return {
          valid: false,
          reason: `Element validation error: ${error.message}`,
        };
      }
    }

    return { valid: true, reason: null };
  }

  // Create an enhanced safe wrapper for MutationObserver
  class EnhancedSafeMutationObserver extends OriginalMutationObserver {
    constructor(callback) {
      // Wrap the callback to handle errors gracefully
      const safeCallback = (mutations, observer) => {
        try {
          return callback(mutations, observer);
        } catch (error) {
          if (DEBUG_MUTATION_OBSERVER) {
            console.warn("MutationObserver callback error:", error);
          }
          // Don't rethrow - just log and continue
        }
      };

      super(safeCallback);
      this._originalCallback = callback;
      this._observedTargets = new Set();
    }

    observe(target, options) {
      const callerInfo = getCallerInfo();

      // Skip observation during page reload instability
      if (isPageReloading || isRecentPageLoad()) {
        if (DEBUG_MUTATION_OBSERVER) {
          console.warn(
            "MutationObserver: Skipping observation during page reload/recent load",
            {
              isReloading: isPageReloading,
              isRecent: isRecentPageLoad(),
              target: typeof target,
              caller: callerInfo,
            },
          );
        }

        // Defer observation until page is stable
        setTimeout(() => {
          if (!isPageReloading && !isRecentPageLoad()) {
            this.observe(target, options);
          }
        }, 500);
        return;
      }

      // Special handling for page reload - check document state first
      if (document.readyState === "loading") {
        if (DEBUG_MUTATION_OBSERVER) {
          console.warn(
            "MutationObserver: Document loading, deferring observation",
            { target, caller: callerInfo },
          );
        }

        // Defer until DOM is ready with additional safety
        const deferredObserve = () => {
          // Add extra delay for page reload scenarios
          setTimeout(() => {
            const revalidation = isValidObserverTarget(target);
            if (revalidation.valid) {
              this._safeObserve(target, options, callerInfo);
            } else if (DEBUG_MUTATION_OBSERVER) {
              console.warn(
                "MutationObserver: Target still invalid after DOM ready",
                revalidation.reason,
              );
            }
          }, 100); // Extra delay for page reload stability
        };

        document.addEventListener("DOMContentLoaded", deferredObserve, {
          once: true,
        });
        return;
      }

      // Enhanced validation with page reload context
      let validation;
      try {
        validation = isValidObserverTarget(target);
      } catch (error) {
        if (DEBUG_MUTATION_OBSERVER) {
          console.warn(
            "MutationObserver: Validation error during page reload",
            {
              error: error.message,
              target: typeof target,
              caller: callerInfo,
            },
          );
        }
        return; // Skip observation if validation throws
      }

      if (!validation.valid) {
        if (DEBUG_MUTATION_OBSERVER) {
          console.warn(`MutationObserver: ${validation.reason}`, {
            target,
            targetType: typeof target,
            targetString: target === "" ? "(empty string)" : String(target),
            options,
            caller: callerInfo,
          });

          // Track problematic library calls
          if (callerInfo) {
            const count = libraryCallTracker.get(callerInfo) || 0;
            libraryCallTracker.set(callerInfo, count + 1);
          }
        }
        return;
      }

      return this._safeObserve(target, options, callerInfo);
    }

    _safeObserve(target, options, callerInfo) {
      try {
        const result = super.observe(target, options);
        this._observedTargets.add(target);

        if (DEBUG_MUTATION_OBSERVER && callerInfo) {
          console.debug("MutationObserver: Successfully observing", {
            target,
            caller: callerInfo,
          });
        }

        return result;
      } catch (error) {
        if (DEBUG_MUTATION_OBSERVER) {
          console.warn("MutationObserver: Error during observe call", {
            error: error.message,
            target,
            options,
            caller: callerInfo,
          });

          // Track error patterns
          const errorKey = `${callerInfo || "unknown"}_${error.message}`;
          const count = libraryCallTracker.get(errorKey) || 0;
          libraryCallTracker.set(errorKey, count + 1);
        }

        // Don't rethrow - just log and continue
        return undefined;
      }
    }

    disconnect() {
      try {
        super.disconnect();
        this._observedTargets.clear();
      } catch (error) {
        if (DEBUG_MUTATION_OBSERVER) {
          console.warn("MutationObserver: Error during disconnect", error);
        }
      }
    }

    // Debug method to get statistics
    static getDebugStats() {
      if (!DEBUG_MUTATION_OBSERVER) {
        return { debug: false };
      }

      return {
        debug: true,
        libraryCallTracker: Object.fromEntries(libraryCallTracker),
        totalProblematicCalls: Array.from(libraryCallTracker.values()).reduce(
          (sum, count) => sum + count,
          0,
        ),
      };
    }
  }

  // Replace the global MutationObserver with our enhanced safe version IMMEDIATELY
  window.MutationObserver = EnhancedSafeMutationObserver;

  // Also patch it on the global object for extra safety
  if (typeof global !== "undefined") {
    global.MutationObserver = EnhancedSafeMutationObserver;
  }

  // Patch any existing references
  if (typeof self !== "undefined") {
    self.MutationObserver = EnhancedSafeMutationObserver;
  }

  if (DEBUG_MUTATION_OBSERVER) {
    console.info("Enhanced MutationObserver fix applied successfully");
  }

  // Add global debug function in development
  if (DEBUG_MUTATION_OBSERVER) {
    window.getMutationObserverDebugStats = () =>
      EnhancedSafeMutationObserver.getDebugStats();

    // Log statistics periodically in development
    let debugInterval;
    const startDebugLogging = () => {
      if (debugInterval) clearInterval(debugInterval);
      debugInterval = setInterval(() => {
        const stats = EnhancedSafeMutationObserver.getDebugStats();
        if (stats.totalProblematicCalls > 0) {
          console.group("MutationObserver Debug Stats");
          console.table(stats.libraryCallTracker);
          console.groupEnd();
        }
      }, 30000); // Log every 30 seconds if there are issues
    };

    // Start logging after initial page load
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () =>
        setTimeout(startDebugLogging, 5000),
      );
    } else {
      setTimeout(startDebugLogging, 5000);
    }
  }

  // Make the class available for exports
  window._EnhancedSafeMutationObserver = EnhancedSafeMutationObserver;
})(); // End immediate execution

// Export for manual use if needed
export const SafeMutationObserver =
  window._EnhancedSafeMutationObserver || window.MutationObserver;
export default SafeMutationObserver;
